<template>
    <canvas
        ref="canvasRef"
        class="report-canvas"
        @mousedown="startDraw"
        @mousemove="draw"
        @mouseup="stopDraw"
        @mouseleave="stopDraw"
    />
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue';

export interface ReportCanvasProps {
    screenshot?: File;
    readonly?: boolean;
};

const props = defineProps<ReportCanvasProps>();

const emit = defineEmits<{
    'update:screenshot': [screenshot: File];
}>();

const canvasRef = ref<HTMLCanvasElement | null>(null);
const context = ref<CanvasRenderingContext2D | null>(null);
const drawing = ref(false);
const moving = ref(false);
const position = ref<{ x: number; y: number }>({
    x: 0,
    y: 0
});
const undoHistory = ref<ImageData[]>([]);
const redoHistory = ref<ImageData[]>([]);

const getPosition = (e: MouseEvent): { x: number; y: number } => {
    if (!canvasRef.value) {
        return {
            x: 0,
            y: 0
        };
    }
	const rect = canvasRef.value.getBoundingClientRect();
	return {
		x: (e.clientX - rect.left) * canvasRef.value.width / rect.width,
		y: (e.clientY - rect.top) * canvasRef.value.height / rect.height
	};
};

const saveState = (): void => {
	if (!context.value || !canvasRef.value) {
		return;
	}
	undoHistory.value.push(context.value.getImageData(0, 0, canvasRef.value.width, canvasRef.value.height));
	redoHistory.value.length = 0;
};

const undo = (): void => {
	if (!context.value || !canvasRef.value || !undoHistory.value.length) {
		return;
	}
	const current = undoHistory.value.pop();
	if (current) {
		redoHistory.value.push(current);
	}
	const previous = undoHistory.value[undoHistory.value.length - 1];
	if (!previous) {
		return;
	}
	context.value.putImageData(previous, 0, 0);
	canvasRef.value.toBlob((blob) => {
		if (!blob) {
			return;
		}
		emit('update:screenshot', new File([blob], 'screenshot.png', { type: 'image/png' }));
	}, 'image/png');
};

const redo = (): void => {
	if (!context.value || !canvasRef.value || !redoHistory.value.length) {
		return;
	}
	const state = redoHistory.value.pop();
	if (!state) {
		return;
	}
	undoHistory.value.push(state);
	context.value.putImageData(state, 0, 0);
	canvasRef.value.toBlob((blob) => {
		if (!blob) {
			return;
		}
		emit('update:screenshot', new File([blob], 'screenshot.png', { type: 'image/png' }));
	}, 'image/png');
};

const startDraw = (e: MouseEvent): void => {
	if (props.readonly) {
		return;
	}
	drawing.value = true;
    moving.value = false;
    position.value = getPosition(e);
};

const draw = (e: MouseEvent): void => {
	if (!context.value || !drawing.value) {
		return;
	}
	if (!moving.value) {
		moving.value = true;
        position.value = getPosition(e);
		return;
	}

    const last = getPosition(e);
	context.value.beginPath();
	context.value.moveTo(position.value.x, position.value.y);
	context.value.lineTo(last.x, last.y);
	context.value.stroke();
    position.value = getPosition(e);
};

const stopDraw = (): void => {
	if (!canvasRef.value || !drawing.value) {
		return;
	}
	drawing.value = false;
	saveState();
	canvasRef.value.toBlob((blob) => {
		if (!blob) {
			return;
		}
		emit('update:screenshot', new File([blob], 'screenshot.png', { type: 'image/png' }));
	}, 'image/png');
};

const onKeyDown = (e: KeyboardEvent): void => {
	if (!(e.ctrlKey || e.metaKey)) {
		return;
	}
	if (e.key.toLowerCase() === 'z' && !e.shiftKey) {
		e.preventDefault();
		undo();
		return;
	}
	if (e.key.toLowerCase() === 'z' && e.shiftKey) {
		e.preventDefault();
		redo();
	}
};

onMounted((): void => {
	if (!canvasRef.value || !props.screenshot) {
		return;
	}
	context.value = canvasRef.value.getContext('2d', {
        willReadFrequently: true
    });
	if (!context.value) {
		return;
	}
	const img = new Image();
	img.crossOrigin = 'anonymous';
	img.src = URL.createObjectURL(props.screenshot);
	img.onload = (): void => {
		if (!canvasRef.value || !context.value) {
			return;
		}
		canvasRef.value.width = img.width;
		canvasRef.value.height = img.height;
		context.value.drawImage(img, 0, 0);
		context.value.strokeStyle = '#ff0000';
		context.value.lineWidth = 3;
		context.value.lineCap = 'round';
		undoHistory.value.length = 0;
		saveState();
	};

	window.addEventListener('keydown', onKeyDown);
});

onBeforeUnmount((): void => {
	window.removeEventListener('keydown', onKeyDown);
});
</script>

<style scoped lang="scss">
@use 'ReportCanvas.scss';
</style>


